import { useEffect, useRef, useState } from "react";

interface UseScrollAnimationOptions {
  threshold?: number;
  triggerOnce?: boolean;
}

const SAFETY_TIMEOUT_MS = 300;

const isMobile = () =>
  typeof window !== "undefined" && window.innerWidth < 768;

export function useScrollAnimation({
  threshold = 0.1,
  triggerOnce = true,
}: UseScrollAnimationOptions = {}) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(() => isMobile());

  useEffect(() => {
    // Guard: already visible (mobile fast-path)
    if (isVisible) return;

    // Guard: no IntersectionObserver (SSR / old browser)
    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }

    // Guard: prefers-reduced-motion
    if (typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setIsVisible(true);
      return;
    }

    const element = ref.current;
    if (!element) {
      setIsVisible(true);
      return;
    }

    let settled = false;

    // Safety timeout: force visible after 2s if IO hasn't fired
    const timer = setTimeout(() => {
      if (!settled) {
        settled = true;
        setIsVisible(true);
      }
    }, SAFETY_TIMEOUT_MS);

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (!settled) {
            settled = true;
            clearTimeout(timer);
          }
          setIsVisible(true);
          if (triggerOnce) {
            observer.unobserve(element);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      { threshold }
    );

    observer.observe(element);

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [threshold, triggerOnce]);

  return { ref, isVisible };
}
